import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, ScrollReveal, StatsBar, CTASection, TestimonialCard } from '../../components/UI';

const GoogleAds = () => {
  const features = [
    {
      icon: '🎯',
      title: 'Strategic Keyword Research',
      desc: 'We don\'t just find keywords—we find the RIGHT keywords. High-intent search terms that indicate someone is ready to buy, not just browsing. We analyze search volume, competition, and cost-per-click to build a keyword strategy that maximizes your ROI.'
    },
    {
      icon: '✍️',
      title: 'Compelling Ad Copy',
      desc: 'Your ads need to stand out in a sea of competitors. We write ad copy that speaks directly to your ideal customer\'s pain points and desires, driving clicks from qualified leads who are ready to take action.'
    },
    {
      icon: '🎨',
      title: 'Landing Page Optimization',
      desc: 'Getting clicks is only half the battle. We ensure your landing pages are optimized to convert those clicks into leads. From page speed to messaging alignment, we optimize every element that affects conversion rates.'
    },
    {
      icon: '📊',
      title: 'Conversion Tracking',
      desc: 'You can\'t improve what you can\'t measure. We set up comprehensive conversion tracking so you know exactly which keywords, ads, and campaigns are driving real business results—not just clicks.'
    },
    {
      icon: '🔄',
      title: 'Continuous Optimization',
      desc: 'Google Ads isn\'t set-it-and-forget-it. We monitor campaigns daily, adjusting bids, testing new ad variations, and refining targeting to continuously improve performance and reduce wasted spend.'
    },
    {
      icon: '📈',
      title: 'Transparent Reporting',
      desc: 'Monthly reports that actually make sense. We show you exactly what\'s working, what we\'re testing, and most importantly—how many leads and customers your ad spend is generating.'
    },
  ];

  const process = [
    { step: '1', title: 'Audit & Strategy', desc: 'We analyze your current campaigns (if any), research your competitors, and develop a strategy tailored to your market and goals.' },
    { step: '2', title: 'Campaign Setup', desc: 'We build your campaigns from scratch—keyword groups, ad copy, targeting settings, and conversion tracking all configured for success.' },
    { step: '3', title: 'Launch & Monitor', desc: 'Campaigns go live with close monitoring. We watch performance data and make rapid adjustments as needed.' },
    { step: '4', title: 'Optimize & Scale', desc: 'Based on data, we optimize what\'s working, cut what\'s not, and scale successful campaigns to drive more leads.' },
  ];

  const pricing = [
    { name: 'Starter', price: '$1,500', adSpend: 'Up to $3K/mo', features: ['1 Campaign', 'Basic Keyword Targeting', 'Monthly Reporting', 'Email Support'] },
    { name: 'Growth', price: '$2,500', adSpend: '$3K - $10K/mo', features: ['Multiple Campaigns', 'Advanced Targeting', 'Bi-weekly Reporting', 'Phone + Email Support'], popular: true },
    { name: 'Scale', price: '$4,000+', adSpend: '$10K+/mo', features: ['Unlimited Campaigns', 'Full Optimization Suite', 'Weekly Reporting', 'Dedicated Manager'] },
  ];

  return (
    <div>
      <PageHeader 
        title="Google Ads Management"
        subtitle="Stop wasting money on ads that don't convert. Our certified specialists create campaigns that drive real leads at lower costs."
        breadcrumb="Google Ads Management"
      />

      <StatsBar />

      {/* Why Google Ads */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <ScrollReveal>
              <div>
                <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Why Google Ads?</span>
                <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 20 }}>
                  Reach Customers at the Exact Moment They're Searching
                </h2>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  Unlike social media advertising where you're interrupting people, Google Ads puts your business in front of customers who are actively searching for your services. They've already identified a need—you're just there to solve it.
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                  The problem? Most businesses waste 40-60% of their Google Ads budget on poor targeting, weak ad copy, and misaligned landing pages. We fix that.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {[
                    { stat: '40%', label: 'Average reduction in cost-per-lead' },
                    { stat: '3x', label: 'Increase in qualified leads' },
                    { stat: '< 7 days', label: 'Time to first results' },
                    { stat: '24/7', label: 'Campaign monitoring' },
                  ].map((item, i) => (
                    <div key={i} style={{ background: '#f8f9fa', borderRadius: 12, padding: 20 }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#ff6b35' }}>{item.stat}</div>
                      <div style={{ color: '#666', fontSize: 14, marginTop: 4 }}>{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', borderRadius: 24, padding: 40 }}>
                <h3 style={{ color: 'white', fontSize: 22, fontWeight: 700, marginBottom: 24 }}>Common Problems We Solve</h3>
                {[
                  'Wasting money on irrelevant clicks',
                  'High cost-per-lead with poor quality',
                  'Competitors outbidding you',
                  'No idea what\'s actually working',
                  'Landing pages that don\'t convert',
                  'Inconsistent lead flow'
                ].map((problem, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <span style={{ color: '#ff6b35', fontSize: 20 }}>✓</span>
                    <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16 }}>{problem}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>What's Included</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Full-Service Google Ads Management
              </h2>
              <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                We handle everything so you can focus on running your business.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
            {features.map((feature, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div style={{ background: 'white', borderRadius: 20, padding: 32, height: '100%', border: '1px solid #eee' }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>{feature.icon}</div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>{feature.title}</h3>
                  <p style={{ color: '#666', fontSize: 15, lineHeight: 1.7, margin: 0 }}>{feature.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Our Process</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10 }}>
                How We Build Winning Campaigns
              </h2>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24 }}>
            {process.map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ textAlign: 'center', padding: 28 }}>
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'linear-gradient(135deg, #ff6b35, #f54e00)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 24, fontWeight: 800, color: 'white' }}>
                    {item.step}
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>{item.title}</h3>
                  <p style={{ color: '#666', fontSize: 14, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Pricing</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Transparent Pricing
              </h2>
              <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 17 }}>
                Management fee based on your ad spend. No hidden fees, no surprises.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {pricing.map((plan, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ 
                  background: 'white', 
                  borderRadius: 20, 
                  padding: 36, 
                  border: plan.popular ? '2px solid #ff6b35' : '1px solid #eee',
                  position: 'relative'
                }}>
                  {plan.popular && (
                    <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#ff6b35', color: 'white', padding: '4px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      Most Popular
                    </div>
                  )}
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{plan.name}</h3>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#ff6b35', marginBottom: 4 }}>{plan.price}<span style={{ fontSize: 16, color: '#888' }}>/mo</span></div>
                  <div style={{ color: '#888', fontSize: 14, marginBottom: 24 }}>Ad spend: {plan.adSpend}</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 28 }}>
                    {plan.features.map((feature, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, color: '#555', fontSize: 15 }}>
                        <span style={{ color: '#22c55e' }}>✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className={plan.popular ? 'btn-primary' : 'btn-secondary'} style={{ display: 'block', textAlign: 'center', padding: 14, borderRadius: 50, fontSize: 15 }}>
                    Get Started
                  </Link>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <p style={{ textAlign: 'center', color: '#888', fontSize: 14, marginTop: 24 }}>
            * Ad spend is billed separately by Google. Custom pricing available for larger budgets.
          </p>
        </div>
      </section>

      {/* Testimonial */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <ScrollReveal>
            <TestimonialCard 
              name="Robert Martinez"
              company="Martinez HVAC"
              text="Finally, an agency that delivers real results. Our Google Ads cost-per-lead dropped by 50% and the quality of leads improved dramatically. We're now booking 3x more jobs than before."
            />
          </ScrollReveal>
        </div>
      </section>

      <CTASection 
        title="Ready to Stop Wasting Ad Spend?"
        subtitle="Let's build a Google Ads strategy that actually generates leads for your business."
        buttonText="Get Your Free Google Ads Audit →"
      />
    </div>
  );
};

export default GoogleAds;
